import React from 'react';
import { TONE, type Tone } from './ui/tokens';

/**
 * The one status badge.
 *
 * It began as an Active/Inactive toggle for User Management, and the copilot needed a badge for
 * order, invoice and stock statuses. A second component would have meant two semantic maps, and
 * they would have disagreed within a release — "Pending" amber in one screen and grey in the
 * other is how a user stops trusting colour at all.
 *
 * So this takes either shape. `active` is the original boolean API and is unchanged, so
 * UserManagement keeps working untouched. `status` is the semantic API: pass the status string
 * the record actually carries and the badge classifies it.
 *
 * COLOUR IS NEVER THE MESSAGE. The label always states the status in words, so the badge reads
 * correctly in monochrome, in print, and to a colour-blind user. Hue is reinforcement.
 */

const POSITIVE = [
  'confirmed', 'completed', 'complete', 'paid', 'available', 'in stock', 'instock', 'active',
  'delivered', 'received', 'approved', 'issued', 'sent', 'done', 'processed', 'won', 'accepted',
  // D144: a vendor PO whose every line is billed in full — nothing left to chase.
  'billed',
];

const ATTENTION = [
  'pending', 'in progress', 'inprogress', 'partial', 'partially paid', 'awaiting', 'draft',
  'review', 'needs review', 'on hold', 'onhold', 'processing', 'ready', 'low', 'low stock',
  // D81: a vendor PO changed since it was sent, waiting to be re-sent.
  'amended',
  // Item 26 (QA retest): a client invoice exists for the order but has not gone out yet.
  'invoiced',
];

const NEGATIVE = [
  'cancelled', 'canceled', 'failed', 'unpaid', 'out of stock', 'outofstock', 'rejected',
  'declined', 'lost', 'error', 'void', 'inactive', 'disputed',
];

/** Overdue is its own band: it is a negative that needs to outrank the other negatives. */
const OVERDUE = ['overdue', 'past due', 'pastdue', 'late'];

/** The status string a record carries -> what it MEANS. Unknown stays neutral, never guessed. */
export function toneForStatus(status?: string | null): Tone {
  const s = String(status || '').trim().toLowerCase().replace(/_/g, ' ');
  if (!s) return 'neutral';
  if (OVERDUE.some((k) => s.includes(k))) return 'overdue';
  if (NEGATIVE.some((k) => s === k || s.includes(k))) return 'negative';
  if (ATTENTION.some((k) => s === k || s.includes(k))) return 'attention';
  if (POSITIVE.some((k) => s === k || s.includes(k))) return 'positive';
  return 'neutral';
}

interface StatusBadgeProps {
  /** Original boolean API. Kept so existing callers are untouched. */
  active?: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
  /** Semantic API: the status as the record records it. */
  status?: string | null;
  /** Override the classification when the caller genuinely knows better. */
  tone?: Tone;
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  active,
  activeLabel = 'Active',
  inactiveLabel = 'Inactive',
  status,
  tone,
  className = '',
}) => {
  const isBooleanApi = status == null && typeof active === 'boolean';
  const label = isBooleanApi ? (active ? activeLabel : inactiveLabel) : String(status ?? '').replace(/_/g, ' ');
  const resolved: Tone = tone
    ?? (isBooleanApi ? (active ? 'positive' : 'negative') : toneForStatus(status));

  if (!label) return null;

  return (
    <span
      className={[
        'inline-flex items-center justify-center rounded-full border px-2 py-1',
        // Sentence case, not ALL CAPS (docs/DESIGN.md). `first-letter` rather than `capitalize`
        // so a two-word status reads "Needs review", the way the app writes every other label.
        'text-xs font-medium leading-none whitespace-nowrap first-letter:uppercase',
        TONE[resolved],
        className,
      ].filter(Boolean).join(' ')}
    >
      {label}
    </span>
  );
};

export default StatusBadge;
